import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import api from '../services/api';
import Toast from './Toast';

/**
 * AnalyticsQA - Chat-like interface for asking COGSLOG analytics questions.
 *
 * Renders in the Dashboard below existing charts. Users type natural language
 * questions about their COGS data and get AI-powered insights.
 *
 * @param {Object} props
 * @param {string|number} [props.businessId] - Active business ID for filtering
 * @component
 */
function AnalyticsQA({ businessId }) {
  const [question, setQuestion] = useState('');
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [showHistory, setShowHistory] = useState(true);

  const exampleQuestions = [
    'Which vendor has the highest spending this month?',
    'What are my top 5 most expensive items?',
    'How does this month compare to last month?',
    'Which items had the biggest price increases?',
  ];

  /**
   * Submit a question to the analytics API.
   * @param {string} q - The question to ask
   */
  const askQuestion = useCallback(async (q) => {
    const trimmed = (q || question).trim();
    if (!trimmed) return;

    setLoading(true);
    setToast(null);

    try {
      const response = await api.askAnalyticsQuestion(trimmed, businessId);
      const result = response.data;

      setConversations(prev => [{
        question: trimmed,
        answer: result.answer,
        dataPoints: result.data_points || [],
        chartSuggestion: result.chart_suggestion,
        tokensUsed: result.tokens_used,
        timestamp: new Date().toLocaleTimeString(),
      }, ...prev]);

      setQuestion('');
    } catch {
      setToast({ message: 'Failed to get answer. Please try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [question, businessId]);

  /**
   * Handle form submission.
   * @param {Event} e - Form submit event
   */
  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    askQuestion();
  }, [askQuestion]);

  return (
    <div className="card analytics-qa">
      <div className="analytics-qa__header">
        <h2 className="analytics-qa__title">Ask COGSLOG About Your Data</h2>
        {conversations.length > 0 && (
          <button
            type="button"
            onClick={() => setShowHistory(!showHistory)}
            className="analytics-qa__toggle-btn"
          >
            {showHistory ? 'Hide' : 'Show'} History ({conversations.length})
          </button>
        )}
      </div>

      {/* Example Questions */}
      {conversations.length === 0 && (
        <div className="analytics-qa__examples">
          <p className="analytics-qa__examples-label">
            Try asking:
          </p>
          <div className="analytics-qa__examples-list">
            {exampleQuestions.map((q, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => askQuestion(q)}
                disabled={loading}
                className="analytics-qa__example-btn"
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input Form */}
      <form onSubmit={handleSubmit} className="analytics-qa__form">
        <input
          type="text"
          value={question}
          onChange={e => setQuestion(e.target.value)}
          placeholder="Ask about your spending, vendors, prices..."
          disabled={loading}
          className="analytics-qa__input"
          aria-label="Analytics question"
        />
        <button type="submit" disabled={loading || !question.trim()}>
          {loading ? 'Thinking...' : 'Ask'}
        </button>
      </form>

      {/* Error */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Loading */}
      {loading && (
        <div className="analytics-qa__loading">
          <img src="/logos/loader/cogslog-loader-light-md.svg" width="48" height="108" alt="Loading..." role="status" />
          <p className="analytics-qa__loading-text">COGSLOG is analyzing your data...</p>
        </div>
      )}

      {/* Conversation History */}
      {showHistory && conversations.map((conv, idx) => (
        <div key={idx} className={`analytics-qa__conversation ${idx > 0 ? 'analytics-qa__conversation--bordered' : ''}`}>
          {/* Question */}
          <div className="analytics-qa__question">
            <strong>Q:</strong> {conv.question}
          </div>

          {/* Answer */}
          <div className="analytics-qa__answer">
            <div
              className="analytics-qa__answer-text"
              dangerouslySetInnerHTML={{
                __html: conv.answer
                  .replace(/&/g, '&amp;')
                  .replace(/</g, '&lt;')
                  .replace(/>/g, '&gt;')
                  .replace(/"/g, '&quot;')
                  .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                  .replace(/\n/g, '<br/>')
              }}
            />
          </div>

          {/* Data Points */}
          {conv.dataPoints && conv.dataPoints.length > 0 && (
            <div className="analytics-qa__data-points">
              {conv.dataPoints.map((dp, i) => (
                <span key={i} className="analytics-qa__data-tag">
                  {dp}
                </span>
              ))}
            </div>
          )}

          {/* Timestamp */}
          <div className="analytics-qa__timestamp">
            {conv.timestamp}
          </div>
        </div>
      ))}
    </div>
  );
}

AnalyticsQA.propTypes = {
  /** Active business ID for filtering analytics queries */
  businessId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default AnalyticsQA;
